import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/api';

const ChatHistoryView = ({ darkMode, onConversationReactivated }) => {
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load archived conversations on mount
  useEffect(() => {
    loadArchivedConversations();
  }, []);

  const loadArchivedConversations = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/agent/conversations/archived/');
      if (response.ok) {
        const data = await response.json();
        setArchivedConversations(data.conversations || []);
        setError(null);
      } else {
        setError('Failed to load conversation history');
      }
    } catch (err) {
      console.error('Error loading archived conversations:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateConversation = async (conversationId, title) => {
    try {
      const response = await apiRequest(`/api/agent/conversations/${conversationId}/reactivate/`, {
        method: 'POST'
      });
      
      if (response.ok) {
        console.log(`🔄 [ChatHistory] Reactivated conversation: ${title}`);
        
        // Notify parent component that conversation was reactivated
        if (onConversationReactivated) {
          onConversationReactivated();
        }
        
        // Reload archived conversations to update the list
        loadArchivedConversations();
      } else {
        console.error('Failed to reactivate conversation');
      }
    } catch (error) {
      console.error('Error reactivating conversation:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className={`flex-1 p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className="animate-spin w-6 h-6 border-2 border-current border-t-transparent rounded-full mx-auto mb-2"></div>
        <p className="text-sm">Loading conversation history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex-1 p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className="mb-4">
          <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="font-medium mb-2">Unable to Load History</h3>
        <p className="text-sm mb-3">{error}</p>
        <button 
          onClick={loadArchivedConversations}
          className="text-blue-500 hover:text-blue-600 text-sm underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (archivedConversations.length === 0) {
    return (
      <div className={`flex-1 p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className="mb-4">
          <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="font-medium mb-2">No Previous Conversations</h3>
        <p className="text-sm">Your archived conversations will appear here</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Previous Conversations
        </h3>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {archivedConversations.length} archived conversation{archivedConversations.length !== 1 ? 's' : ''}
        </p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {archivedConversations.map(conv => (
          <div 
            key={conv.id}
            className={`
              border rounded-lg p-3 transition-colors hover:bg-opacity-50
              ${darkMode 
                ? 'border-gray-700 bg-gray-800 hover:bg-gray-700' 
                : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
              }
            `}
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className={`font-medium text-sm line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {conv.title}
              </h4>
            </div>
            
            <p className={`text-xs line-clamp-2 mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {conv.preview}
            </p>
            
            <div className="flex justify-between items-center">
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {conv.message_count} message{conv.message_count !== 1 ? 's' : ''} • {formatDate(conv.updated_at)}
              </span>
              
              <button 
                onClick={() => handleReactivateConversation(conv.id, conv.title)}
                className={`
                  text-xs px-2 py-1 rounded transition-colors
                  ${darkMode 
                    ? 'text-blue-400 hover:bg-blue-900/30 hover:text-blue-300' 
                    : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                  }
                `}
              >
                Continue
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChatHistoryView;
